import * as THREE from 'three';
import { tierUrl, type TextureTier } from './manifest';
import { selectFirstPaintTier, maxTierForGpu } from './device-detect';
import { TIER_ORDER } from './manifest';

export interface TextureLoadEvent {
  tier: TextureTier;
  texture: THREE.Texture;
  /** Milliseconds from load-start to ready. */
  durationMs: number;
}

export interface TextureManagerOptions {
  gpuMaxTextureSize: number;
  baseUrl?: string;
  /**
   * Called once the target tier's texture is ready. The caller is
   * responsible for swapping it onto the globe material.
   */
  onLoad: (event: TextureLoadEvent) => void;
  /** Called on fetch failure. No retry or fallback — stays on blank sphere. */
  onError?: (error: Error, tier: TextureTier) => void;
}

/**
 * Loads the single appropriate texture tier for the device — no progressive
 * cycling. The right tier is picked up-front from GPU + device signals,
 * downloaded once, and applied when ready. Total bandwidth: one file.
 *
 * The loading UX (spinner overlay) hides the wait, so perceived snappiness
 * comes from the overlay not from cycling through low-quality previews.
 */
export class TextureManager {
  private disposed = false;
  private readonly baseUrl: string;
  private readonly onLoad: (event: TextureLoadEvent) => void;
  private readonly onError?: (error: Error, tier: TextureTier) => void;
  private readonly gpuMax: number;

  constructor(options: TextureManagerOptions) {
    this.gpuMax = options.gpuMaxTextureSize;
    this.baseUrl = options.baseUrl ?? '';
    this.onLoad = options.onLoad;
    this.onError = options.onError;
  }

  async start(): Promise<void> {
    await this.loadAndApply(selectFirstPaintTier(this.gpuMax));
  }

  /**
   * Load a specific tier manually and apply it. Used by the debug panel for
   * visual comparison across tiers. Bypasses auto-selection entirely BUT
   * still refuses tiers above the GPU's MAX_TEXTURE_SIZE — uploading a
   * texture larger than the GPU permits is undefined behavior in WebGL
   * (some drivers silently fail, others render the texture upside-down or
   * as garbage). Returns silently with a console warning if blocked.
   */
  async loadTier(tier: TextureTier): Promise<void> {
    const ceiling = maxTierForGpu(this.gpuMax);
    if (TIER_ORDER.indexOf(tier) > TIER_ORDER.indexOf(ceiling)) {
      console.warn(
        `[atlas] refusing to load "${tier}" texture: exceeds GPU MAX_TEXTURE_SIZE ` +
          `(${this.gpuMax}); ceiling for this device is "${ceiling}"`,
      );
      return;
    }
    await this.loadAndApply(tier);
  }

  dispose(): void {
    this.disposed = true;
  }

  private async loadAndApply(tier: TextureTier): Promise<void> {
    try {
      const event = await this.load(tier);
      if (this.disposed) {
        event.texture.dispose();
        return;
      }
      this.onLoad(event);
    } catch (err) {
      this.onError?.(err as Error, tier);
    }
  }

  private async load(tier: TextureTier): Promise<TextureLoadEvent> {
    const url = this.baseUrl ? tierUrl(tier, this.baseUrl) : tierUrl(tier);
    const start = performance.now();
    const texture = await loadTextureFromUrl(url);
    texture.colorSpace = THREE.SRGBColorSpace;
    return { tier, texture, durationMs: performance.now() - start };
  }
}

/**
 * Load a texture using ImageBitmapLoader when available — decodes the JPEG
 * off the main thread via createImageBitmap, dramatically reducing stall
 * during the swap. Falls back to TextureLoader (main-thread decode) in
 * environments without createImageBitmap.
 */
function loadTextureFromUrl(url: string): Promise<THREE.Texture> {
  const supportsImageBitmap = typeof createImageBitmap === 'function';

  if (supportsImageBitmap) {
    return new Promise((resolve, reject) => {
      const loader = new THREE.ImageBitmapLoader();
      // imageOrientation 'flipY' pre-flips the bitmap during decode so it lands
      // in the orientation Three.js expects for WebGL texture space. Critical:
      // ImageBitmap sources IGNORE texture.flipY entirely (browsers don't
      // support flipping ImageBitmaps at upload time), so the flip has to
      // happen at decode time or the result renders upside down.
      loader.setOptions({ imageOrientation: 'flipY', premultiplyAlpha: 'none' });
      loader.load(
        url,
        (bitmap) => {
          const texture = new THREE.CanvasTexture(bitmap as unknown as HTMLCanvasElement);
          resolve(texture);
        },
        undefined,
        (error: unknown) => {
          const message = error instanceof ErrorEvent ? error.message : String(error);
          reject(new Error(`Failed to load texture ${url}: ${message}`));
        },
      );
    });
  }

  return new Promise((resolve, reject) => {
    const loader = new THREE.TextureLoader();
    loader.load(
      url,
      (texture) => resolve(texture),
      undefined,
      (error: unknown) => {
        const message = error instanceof ErrorEvent ? error.message : String(error);
        reject(new Error(`Failed to load texture ${url}: ${message}`));
      },
    );
  });
}
